import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import Scrollbar from 'smooth-scrollbar'
import GridHorizontalSection from '../../animations/GridHorizontalSection'
import Contents from '../../components/Contents'
import Globe from '../../components/Globe'
import Page from '../../components/Page'
import ParallaxImage from '../../components/ParallaxImage'
import Video from '../../components/Video'
import { getBodyFontSizeFloat, mapEach } from '../../utils/dom'
import ScrollingMarquee from '../../components/ScrollingMarquee'
import imagesLoaded from 'imagesloaded'
import SplitText from 'gsap/SplitText'
import { wrap } from '../../utils/text'
import DetectionManager from '../../classes/Detection'
import PortraitView from '../../components/PortraitView'
import SocialShare from '../../components/SocialShare'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import { scrollTo, scrollToInner } from '../../utils/scroll'
import { getColorForProgram } from '../../utils/colors'
import Menu from '../../components/Menu'
import DJ from '../../components/DJ'

export default class extends Page {
  constructor () {
    super({
      classes: {
        active: 'page--active'
      },
      element: 'body',
      elements: {
        scrollContainer: '.scroll-container',
        animateIn: '[data-animate]',
        firstSection: '.first-section',
        intro: '.intro',
        introSlider: '.intro__slider',
        sliderFirst: '.intro__slider__first',
        sliderSecond: '.intro__slider__second',
        sliderThird: '.intro__slider__third',
        sliderThirdPicture: '.intro__slider__third__content picture img',
        sliderFourth: '.intro__slider__fourth',
        clareImage: '.intro__slider__fourth__content picture',
        clareImageInner: '.intro__slider__fourth__content picture img',
        // Impact Snapshot
        impactSnapshotContainer: '.impact-snapshot',
        impactSnapshotBottom: '.impact-snapshot__title__bottom',
        impactSnapshotTitle: '.impact-snapshot__title',
        impactSnapshotList: '.impact-snapshot__snapshot__stats__list',
        secondSection: '.second-section',
        impactSnapshot: '.impact-snapshot__snapshot',
        // Global story
        globeBlueContent: '.global-story__globe-content .blue-content',
        globalStory: '.global-story',
        globalStoryMask: '.global-story__mask',
        // Three C
        christCentred: '.three-c__slide:nth-child(1)',
        churchBased: '.three-c__slide:nth-child(2)',
        childFocused: '.three-c__slide:nth-child(3)',
        // Test
        largeKidsImage: '.intro__slider__third__content figure',
        largestKidsImageInner: '.intro__slider__third__content picture',
        // Contents
        contentsLinks: '.intro__slider__second__content ul li a',
        // Grid
        gridImageContainers: '[data-parallax-image]',
        // Programs
        programs: '.program',
        programSections: '[data-program-section]',
        eighthRow: '.eighth-row',
        programVideos: '.program-video',
        // Thankyou
        thankyou: '.thankyou',
        thankyouNext: '.thankyou__next',
        thankyouImage: '.thankyou__image',
        thankyouMask: '.thankyou__mask',
        thankyouDivider: '.thankyou__divider',
        // Lazy images
        lazyImages: '.lazy',
        preloader: '.preloader',
        preloaderDigits: '.preloader__digit',
        preloaderFirstDigit: '.preloader__digit:nth-child(1) .preloader__digit__inner',
        preloaderSecondDigit: '.preloader__digit:nth-child(2) .preloader__digit__inner',
        preloaderThirdDigit: '.preloader__digit:nth-child(3) .preloader__digit__inner',
        preloaderFirstLine: '.intro__slider__first__content__border',
        preloaderSecondLine: '.intro__slider__second__content__border',
        preloaderThirdLine: '.intro__slider__third__content__border',
        contentsVideo: '.intro__slider__first__content figure',
        contentsShareLinks: '.intro__slider__first__content__share .share-icons li',
        contentsDownloadAnnualReport1: '.intro__content__ctas__download1',
        contentsDownloadAnnualReport2: '.intro__content__ctas__download2',
        introSoundDisclaimer: '.intro__content__sound-disclaimer',
        introTitle: '.intro__content h1',
        introLogo: '.intro__content__logo',
        contentsCtas: '.intro__content__ctas',
        contentsShareTitle: '.intro__slider .intro__slider__first__content__share h3',
        shareIcons: '.share-icons',
        contentsContentsTitle: '.intro__slider__second__content__title',
        // Contents navs
        ourApproach: '#our-approach',
        ourPrograms: '#our-programs',
        thankyouFinal: '#thankyou',
        scrollbarProgress: '.scrollbar-progress',
        scrollbarProgressInner: '.scrollbar-progress__inner',
        nav: '.nav',
        // Scroll to Explore
        scrollExploreContentContainer: '.intro__slider__second__content__scroll__content',
        scrollExplore1: '.intro__slider__second__content__scroll__content__1',
        scrollExplore2: '.intro__slider__second__content__scroll__content__2',
        scrollExploreIconInner: '.intro__slider__second__content__scroll__icon__inner',
        scrollExploreIcon: '.intro__slider__second__content__scroll__icon',
        preloaderEnter: '.preloader-enter',
        programLinkGroup: '.program-link-group',
        programMumsBabies: '#program-mb',
        programsCriticalNeeds: '#program-cn',
        tickLinks: '[data-tick]'
      }
    })
    gsap.registerPlugin(SplitText, ScrollToPlugin)
    this.sectionTimelines = {
      first: null
    }
    this.scrollDirection = 'up'
  }

  /**
   * Methods.
   */
  create () {
    super.create()
    this.createDJ()
    this.createPortraitView()
    this.createPreloader()
  }

  createMenu () {
    this.menu = new Menu(this.scrollbar, this.DJ)
  }

  createDJ () {
    this.DJ = new DJ()
  }

  createLinkSounds () {
    this.elements.tickLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        this.DJ.playTick()
      })
    })
  }

  createMenuLinks () {
    this.elements.contentsLinks.forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault()
        const index = Number(e.target.dataset.index)
        // Calculate by running the animation, getting the scroll from container
        // Then do scroll / windowheight to get the ratio used below

        // 2021 Impact
        if (index === 1) {
          scrollToInner(2.2548, this.scrollbar)
        // Global Impact
        } else if (index === 2) {
          scrollToInner(9.296, this.scrollbar)
        // Our Distinctives
        } else if (index === 3) {
          scrollToInner(11.867, this.scrollbar)
        // Our Approach
        } else if (index === 4) {
          scrollTo(this.elements.ourApproach, this.scrollbar)
        } else if (index === 5) {
          scrollTo(this.elements.ourPrograms, this.scrollbar)
        } else if (index === 6) {
          scrollToInner(999999, this.scrollbar)
        }
      })
    })
  }

  createProgramLinks () {
    this.elements.programLinkGroup.forEach((link, index) => {
      link.addEventListener('click', e => {
        e.preventDefault()
        scrollTo(document.querySelector(link.dataset.href), this.scrollbar)
      })
    })
  }

  createPortraitView () {
    this.components.push(new PortraitView())
  }

  createPreloader () {
    this.loadedImageCount = 0
    const imgLoad = imagesLoaded('body')
    this.imageCount = imgLoad.images.length

    this.downloadAnnualReport1Words = new SplitText(this.elements.contentsDownloadAnnualReport1, {
      type: 'words',
      wordsClass: 'word'
    })
    this.downloadAnnualReport1Words.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })

    this.downloadAnnualReport2Words = new SplitText(this.elements.contentsDownloadAnnualReport2, {
      type: 'words',
      wordsClass: 'word'
    })
    this.downloadAnnualReport2Words.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })

    this.scrollExplore1Words = new SplitText(this.elements.scrollExplore1, {
      type: 'words',
      wordsClass: 'word'
    })
    this.scrollExplore1Words.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })
    this.scrollExplore2Words = new SplitText(this.elements.scrollExplore2, {
      type: 'words',
      wordsClass: 'word'
    })
    this.scrollExplore2Words.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })

    this.preloaderEnterWords = new SplitText(this.elements.preloaderEnter, {
      type: 'chars',
      charsClass: 'word'
    })
    this.preloaderEnterWords.chars.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })

    gsap.set([this.elements.contentsCtas, this.elements.scrollExploreContentContainer, this.elements.preloaderEnter], {
      opacity: 1
    })

    imgLoad.on('progress', (instance, image) => {
      this.loadedImageCount++
      const progress = Math.round(this.loadedImageCount / this.imageCount * 100)
      const progressString = progress.toString()
      const paddedProgress = progressString.padStart(3, '0')

      // Get digits
      const digit1 = Number(paddedProgress[0])
      const digit2 = Number(paddedProgress[1])

      gsap.to(this.elements.preloaderFirstDigit, {
        translateY: `-${digit1 * 9.09}%`,
        ease: 'power2.out',
        duration: 2,
        delay: 0.8
      })
      if (digit2 === 0) {
        gsap.to(this.elements.preloaderSecondDigit, {
          translateY: '-90.91%',
          ease: 'power2.out',
          duration: 2
        })
      } else {
        gsap.to(this.elements.preloaderSecondDigit, {
          translateY: `-${digit2 * 9.09}%`,
          ease: 'power2.out',
          duration: 2
        })
      }

      // gsap.to(this.elements.preloaderThirdDigit, {
      //   translateY: `-${Number(digit3) * 10}%`,
      //   ease: 'power2.out',
      //   duration: 2
      // })

      // Complete
      if (paddedProgress === '100') {
        gsap.timeline({
          onComplete: () => {
            this.elements.preloaderEnter.addEventListener('click', () => {
              if (this.isAnimatingEntered) {
                return
              }
              this.isAnimatingEntered = true
              this.onEntered()
            })
            this.onLoaded()
          }
        })
          .to(this.elements.preloaderDigits, {
            translateY: '-110%',
            stagger: 0.1,
            duration: 1.4,
            delay: 2,
            ease: 'expo.inOut'
          })
          .to(this.preloaderEnterWords.chars, {
            y: 0,
            ease: 'expo.out',
            duration: 1.4,
            stagger: 0.08
          }, '-=0.4')
      }
    })
  }

  onEntered () {
    this.DJ.playBackground()
    gsap.set('body', {
      position: 'static'
    })
    if (this.scrollbar) {
      this.scrollbar.updatePluginOptions('disableScroll', {
        paused: false
      })
    }
    gsap.timeline({
      onComplete: () => {
        gsap.set([this.elements.preloaderEnter, this.elements.preloader, this.elements.introSoundDisclaimer], {
          opacity: 0,
          pointerEvents: 'none'
        })
        gsap.set(this.elements.introSlider, {
          opacity: 1
        })
        gsap.to(this.elements.preloaderFirstLine, {
          clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)',
          duration: 1,
          ease: 'expo.inOut'
        })
        gsap.to(this.elements.preloaderSecondLine, {
          clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)',
          duration: 1,
          delay: 0.5,
          ease: 'power3.inOut'
        })
        gsap.to(this.elements.preloaderThirdLine, {
          clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)',
          duration: 1,
          delay: 1,
          ease: 'power1.inOut'
        })
        gsap.set(this.elements.preloader, {
          display: 'none'
        })
        gsap.to(this.elements.contentsLinks, {
          translateY: 0,
          duration: 2,
          ease: 'expo.out',
          delay: 1,
          stagger: 0.1,
          pointerEvents: 'auto'
        })
        gsap.to(this.elements.contentsVideo, {
          opacity: 1,
          duration: 1.5,
          delay: 1,
          ease: 'expo.out'
        })
        gsap.to(this.elements.contentsVideo, {
          opacity: 1,
          duration: 4,
          ease: 'expo.out',
          delay: 1
        })
        gsap.to(this.elements.contentsShareLinks, {
          opacity: 1,
          duration: 4,
          ease: 'expo.out',
          stagger: 0.05,
          delay: 1
        })
        // Setup download animation
        gsap.timeline({
          repeat: -1
        })
          .fromTo(this.downloadAnnualReport1Words.words, {
            y: '105%'
          }, {
            y: '0%',
            duration: 0.7,
            stagger: 0.05,
            pointerEvents: 'auto',
            ease: 'expo.out'
          })
          .to({}, {
            duration: 4
          })
          .to(this.downloadAnnualReport1Words.words, {
            y: '-105%',
            duration: 0.7,
            stagger: 0.05,
            pointerEvents: 'none',
            ease: 'expo.in'
          })
          .fromTo(this.downloadAnnualReport2Words.words, {
            y: '105%'
          }, {
            y: '0%',
            duration: 0.7,
            stagger: 0.05,
            ease: 'expo.out'
          })
          .to({}, {
            duration: 4
          })
          .to(this.downloadAnnualReport2Words.words, {
            y: '-105%',
            duration: 0.7,
            stagger: 0.05,
            ease: 'expo.in'
          })

        // Setup scroll to explore animation
        gsap.to(this.elements.scrollExploreIcon, {
          opacity: 1,
          duration: 0.3,
          ease: 'power2.inOut'
        })
        gsap.to(this.elements.scrollExploreIconInner, {
          top: '2rem',
          ease: 'power4.inOut',
          opacity: 0,
          duration: 3,
          repeat: -1,
          delay: 4
        }, 0)
        gsap.timeline({
          repeat: -1
        })
          .fromTo(this.scrollExplore1Words.words, {
            y: '105%'
          }, {
            y: '0%',
            duration: 0.7,
            stagger: 0.05,
            pointerEvents: 'auto',
            ease: 'expo.out'
          })
          .to({}, {
            duration: 1.5
          })
          .to(this.scrollExplore1Words.words, {
            y: '-105%',
            duration: 0.7,
            stagger: 0.05,
            pointerEvents: 'none',
            ease: 'expo.in'
          })
          .fromTo(this.scrollExplore2Words.words, {
            y: '105%'
          }, {
            y: '0%',
            duration: 0.7,
            stagger: 0.05,
            ease: 'expo.out'
          })
          .to({}, {
            duration: 1.5
          })
          .to(this.scrollExplore2Words.words, {
            y: '-105%',
            duration: 0.7,
            stagger: 0.05,
            ease: 'expo.in'
          })
      }
    })
      .to(this.preloaderEnterWords.chars, {
        y: '-100%',
        ease: 'expo.in',
        duration: 1.2,
        stagger: 0.04
      })
      .to(this.elements.introTitle, {
        color: 'rgb(0, 66, 191',
        duration: 1,
        ease: 'expo.in'
      }, 0.4)
      .to(this.elements.introLogo.querySelectorAll('path'), {
        fill: 'rgb(0, 66, 191',
        duration: 1,
        ease: 'expo.in'
      }, 0.4)
      .to(this.elements.intro, {
        background: 'white',
        duration: 1,
        ease: 'expo.in'
      }, 0.4)
  }

  createThankyou () {
    this.thankyouScroller = new ScrollingMarquee(this.elements.thankyouNext, null, 'left', DetectionManager.isMobileOrTablet() ? this.elements.thankyouNext.clientWidth / 5 : this.elements.thankyouNext.clientWidth / 40, true)
    const thankyouDivider = this.elements.thankyouDivider
    this.thankyouTimeline = gsap.timeline({
      scrollTrigger: {
        invalidateOnRefresh: true,
        trigger: this.elements.thankyou,
        pin: true,
        start: 'top top',
        end: () => {
          return DetectionManager.isMobileOrTablet() ? `+=${window.outerHeight * 2}` : '+=200%'
        },
        scrub: true,
        toggleClass: 'active',
        onEnter: () => {
          this.DJ.playKidsPlaying()
        }
      }
    })
      .to(this.elements.nav, {
        color: '#0042BF',
        background: 'white',
        borderColor: '#0042BF',
        duration: 20
      })
      .fromTo(this.elements.thankyouImage, {
        width: '100vw',
        height: '100vh'
      }, {
        width: '25vh',
        height: '25vh',
        duration: 600,
        ease: 'none'
      }, '-=20')
      .to(this.elements.thankyouMask, {
        clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)',
        duration: 1080,
        onUpdate () {
          const progress = this.progress()
          gsap.set(thankyouDivider, {
            top: `${100 - progress * 100}%`
          })
        },
        ease: 'none'
      }, '-=300')
  }

  createGlobe () {
    if (!this.elements.firstSection) {
      return
    }
    this.globe = new Globe()
  }

  createProgramScrolls () {
    this.programScrollTimelines = []
    const components = this.components
    const elements = this.elements
    const DJ = this.DJ
    mapEach(this.elements.programs, program => {
      this.programScrollTimelines.push(gsap.timeline({
        scrollTrigger: {
          invalidateOnRefresh: true,
          trigger: program,
          pin: true,
          start: 'top top',
          end: () => {
            return DetectionManager.isMobileOrTablet() ? `+=${window.outerHeight * 6}` : '+=600%'
          },
          scrub: true,
          toggleClass: 'active',
          onEnter () {
            gsap.set(elements.scrollbarProgressInner, {
              background: getColorForProgram(program)
            })
            const iframes = program.querySelectorAll('iframe')
            if (iframes) {
              mapEach(iframes, iframe => {
                if (iframe.dataset.src) {
                  iframe.src = iframe.dataset.src
                  delete iframe.dataset.src
                }
              })
              components.push(new Video(program.querySelector('.program-video'), DJ))
            }
          },
          onEnterBack () {
            gsap.set(elements.scrollbarProgressInner, {
              background: getColorForProgram(program)
            })
          },
          onLeaveBack () {
            gsap.set(elements.scrollbarProgressInner, {
              background: '#0042BF'
            })
          },
          onLeave () {
            gsap.set(elements.scrollbarProgressInner, {
              background: '#0042BF'
            })
          }
        }
      })
        .to(program.querySelector('.program__inner'), {
          translateX: () => {
            let x = 0
            Array.from(program.querySelectorAll('section')).forEach((section) => {
              x += section.clientWidth
            })
            // Remove first slide width
            x -= window.innerWidth
            return `-${x}px`
          },
          duration: 800,
          ease: 'none'
        })
        .to(this.elements.nav, {
          color: getColorForProgram(program),
          background: 'white',
          borderColor: getColorForProgram(program),
          duration: 20
        }, 0)
        .fromTo(program.querySelector('.program-final__image'), {
          width: '100vw',
          height: '100%'
        }, {
          width: '93rem',
          height: '50vh',
          ease: 'none',
          duration: 75
        })
        .fromTo(program.querySelector('.program-final__in'), {
          top: '100%'
        }, {
          top: '75%',
          ease: 'none',
          duration: 75
        }, '-=75')
        .to(this.elements.nav, {
          color: getColorForProgram(program),
          background: 'white',
          borderColor: getColorForProgram(program)
        }, {
          color: 'black',
          background: 'white',
          borderColor: 'black',
          duration: 20
        }, '-=75'))
    })
    this.setScrollFixes()
  }

  setScrollFixes () {
    mapEach(this.elements.programs, program => {
      let nextRow = null
      if (program.classList.contains('program--mb')) {
        nextRow = document.querySelector('.eighth-row')
      } else if (program.classList.contains('program--cs')) {
        nextRow = document.querySelector('.tenth-row')
      } else if (program.classList.contains('program--cn')) {
        nextRow = document.querySelector('.eleventh-row')
      }
      const finalElHeight = program.querySelector('.program-final__in').clientHeight
      const offset = (finalElHeight - window.innerHeight * 0.25) + 2 * getBodyFontSizeFloat()
      gsap.set(nextRow, {
        marginTop: `${offset}px`
      })
    })
  }

  createScroll () {
    if (!DetectionManager.isMobileOrTablet()) {
      const scroller = this.elements.scrollContainer

      this.scrollbar = Scrollbar.init(scroller, {
        damping: 0.1,
        delegateTo: document,
        alwaysShowTracks: true,
        plugins: {
          disableScroll: {
            direction: 'x',
            paused: true
          }
        }
      })

      this.scrollbar.track.xAxis.element.remove()
      this.scrollbar.track.yAxis.element.remove()

      const scrollbar = this.scrollbar

      ScrollTrigger.scrollerProxy(this.elements.scrollContainer, {
        scrollTop (value) {
          if (arguments.length) {
            scrollbar.scrollTop = value
          }
          return scrollbar.scrollTop
        }
      })

      ScrollTrigger.defaults({ scroller: scroller })

      this.scrollbar.addListener(listener => {
        const progress = listener.offset.y / listener.limit.y * 100
        gsap.set(this.elements.scrollbarProgressInner, {
          width: `${progress.toFixed(2)}%`
        })
        ScrollTrigger.update()
      })
    } else {
      document.addEventListener('scroll', () => {
        const progress = window.scrollY / (document.body.clientHeight - window.innerHeight) * 100
        gsap.set(this.elements.scrollbarProgressInner, {
          width: `${progress.toFixed(2)}%`
        })
      })
    }
  }

  createFirstScrollSection () {
    if (!this.elements.firstSection) {
      return
    }

    this.blueScroll = {
      x1: 100,
      x2: 100
    }
    gsap.set(this.elements.impactSnapshotBottom, {
      translateX: '50%'
    })
    gsap.set(this.elements.largestKidsImageInner, {
      translateX: `-${this.elements.largeKidsImage.clientWidth / 150}rem`
    })

    this.mainPageTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.elements.firstSection,
        pin: true,
        start: 'top left',
        end: () => DetectionManager.isMobileOrTablet() ? window.outerHeight * 14 : '+=1400%',
        scrub: true,
        invalidateOnRefresh: true
      }
    })
      .to(this.elements.sliderFirst, {
        translateX: '-98rem',
        duration: 98,
        ease: 'none'
      })
      .to(this.elements.sliderSecond, {
        translateX: '-46rem',
        duration: 46,
        ease: 'none',
        onStart: () => {
          this.DJ.playSchoolAndSinging()
        }
      })
      .to(this.elements.sliderThird, {
        translateX: '-220rem',
        duration: 220,
        ease: 'none'
      })
      .to(this.elements.intro, {
        translateY: '-100%',
        duration: 192,
        ease: 'none'
      })
      .to(this.elements.nav, {
        background: '#0042BF',
        color: 'white',
        borderColor: 'white',
        opacity: 1,
        duration: 20,
        pointerEvents: 'auto'
      })
      .to(this.elements.impactSnapshotBottom, {
        translateX: '-100%',
        ease: 'none',
        duration: 540
      }, '-=212')
      .to(this.elements.impactSnapshot, {
        translateY: '-100%',
        ease: 'none',
        duration: 192
      })
      .to(this.elements.impactSnapshotList, {
        translateY: '-60%',
        ease: 'none',
        duration: 192
      })
      .to(this.elements.impactSnapshotContainer, {
        translateY: '-100%',
        ease: 'none',
        duration: 192
      })
      .to(this.elements.nav, {
        color: '#0042BF',
        background: 'white',
        borderColor: '#0042BF',
        duration: 20,
        pointerEvents: 'auto'
      })
      .to(this.elements.globalStory, {
        opacity: 1,
        ease: 'none',
        duration: 192
      }, '-=212')
      .to(this.blueScroll, {
        x1: 0,
        ease: 'power1.in',
        duration: 192
      })
      .to(this.blueScroll, {
        x2: 0,
        ease: 'power2.in',
        duration: 192,
        onUpdate: () => {
          gsap.set(this.elements.globeBlueContent, {
            clipPath: `polygon(0 ${this.blueScroll.x1}%, 100% ${this.blueScroll.x2}%, 100% 100%, 0% 100%)`
          })
        }
      }, '-=192')
      .to(this.elements.nav, {
        color: 'white',
        background: '#0042BF',
        borderColor: 'white',
        duration: 20
      })
      .to(this.blueScroll, {
        x2: 0,
        ease: 'power2.in',
        duration: 192,
        onUpdate: () => {
          gsap.set(this.elements.globeBlueContent, {
            clipPath: `polygon(0 ${this.blueScroll.x1}%, 100% ${this.blueScroll.x2}%, 100% 100%, 0% 100%)`
          })
        }
      }, '-=212')
      .to(this.globe, {
        rotationY: 1.5,
        ease: 'none',
        duration: 576
      }, '-=384')
      .to(this.elements.globalStoryMask, {
        opacity: 1,
        ease: 'none',
        duration: 192
      }, '-=192')
      .to(this.elements.christCentred, {
        translateX: '0%',
        duration: 192,
        ease: 'none'
      }, '-=192')
      .to(this.elements.nav, {
        color: 'black',
        background: 'white',
        borderColor: 'black',
        duration: 20
      })
      .to(this.elements.churchBased, {
        translateX: '5.2%',
        duration: 182,
        ease: 'none'
      }, '-=20')
      .to(this.elements.childFocused, {
        translateX: '10.4%',
        duration: 172,
        ease: 'none'
      })
      // Fixed
      .to(this.elements.largestKidsImageInner, {
        translateX: `${this.elements.largeKidsImage.clientWidth / 150}rem`,
        duration: 364,
        ease: 'none'
      }, 0)
  }

  createComponents () {
    if (!this.elements.firstSection) {
      return
    }
    super.createComponents()
    this.contents = new Contents()
    mapEach(this.elements.shareIcons, group => {
      this.components.push(new SocialShare(group))
    })
  }

  createAnimations () {
    mapEach(this.elements.programSections, section => {
      this.animations.push(new GridHorizontalSection(section))
    })
  }

  createParallaxImages () {
    mapEach(this.elements.gridImageContainers, image => {
      this.components.push(new ParallaxImage({
        element: image
      }))
    })
  }

  onLoaded () {
    this.isLoaded = true
    this.createScroll()
    this.createMenu()
    this.createGlobe()
    this.createFirstScrollSection()
    this.createProgramScrolls()
    this.createThankyou()
    this.createMenuLinks()
    this.createProgramLinks()
    this.createParallaxImages()
    this.createLinkSounds()
    this.onResize()
  }

  onResize () {
    super.onResize()
    if (DetectionManager.isMobileOrTablet()) {
      document.body.classList.add('mobile-tablet')
    } else {
      document.body.classList.remove('mobile-tablet')
    }
    if (!this.elements.firstSection || !this.isLoaded) {
      return
    }
    this.globe.onResize()
    this.setScrollFixes()
    if (DetectionManager.isMobileOrTablet()) {
      if (this.scrollbar) {
        window.location.reload()
      }
    } else {
      if (this.scrollbar) {
        this.scrollbar.update()
      } else {
        window.location.reload()
      }
    }
  }

  /**
   * Animations.
   */
  async show (url) {
    return super.show(url)
  }

  async hide (url) {
    this.element.classList.remove(this.classes.active)
    return super.hide(url)
  }

  update () {
    this.globe && this.globe.update()
    this.menu && this.menu.isOpen && this.menu.update()
  }
}
